import { type JSX, type Ref, useImperativeHandle, useState } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useIslandHost } from "@frontend/contexts";

import { ProjectEditor } from "./ProjectEditor";
import {
  type ProjectEditorDataProps,
  projectEditorDataSchema,
} from "./ProjectEditor.schema";
import { ProjectEditorDialog } from "./ProjectEditorDialog";
import { ProjectEditorWindow } from "./ProjectEditorWindow";

export type ProjectEditorHandle = {
  open: (projectId?: number | null) => void;
  hide: () => void;
};

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    "project-editor": {
      props: ProjectEditorDataProps;
      handle: ProjectEditorHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-project-editor": HTMLElement & ProjectEditorHandle;
  }
}

export function RWCProjectEditor({
  ref,
  projectId: initialProjectId,
  type,
  contained,
  shell,
  open: initialOpen,
}: ProjectEditorDataProps & { ref?: Ref<ProjectEditorHandle> }): JSX.Element {
  const host = useIslandHost();

  const [projectId, setProjectId] = useState<number | null>(initialProjectId);

  const [open, setOpen] = useState(initialOpen);

  useImperativeHandle(
    ref,
    () => ({
      open: (next = null) => {
        setProjectId(next ?? null);
        setOpen(true);
      },
      hide: () => setOpen(false),
    }),
    [],
  );

  function emit(eventType: string, detail: Record<string, unknown> = {}) {
    host?.dispatchEvent(new CustomEvent(eventType, { detail, bubbles: true }));
  }

  const onSaved = (savedId: number) =>
    emit("project:saved", { projectId: savedId });

  const onCancel = () => emit("project:cancel");

  if (contained) {
    return (
      <ProjectEditor
        projectId={projectId}
        type={type}
        onSaved={onSaved}
        onCancel={onCancel}
      />
    );
  }

  const Shell = "window" === shell ? ProjectEditorWindow : ProjectEditorDialog;

  return (
    <Shell
      open={open}
      onOpenChange={setOpen}
      projectId={projectId}
      type={type}
      onSaved={onSaved}
      onCancel={onCancel}
    />
  );
}

componentManager.register(
  rwc("project-editor", {
    component: RWCProjectEditor,
    dataSchema: projectEditorDataSchema,
  }),
);
